import React from "react";
import { FormattedMessage } from "react-intl";
import { connect, useSelector } from "react-redux";

import { CancelIcon, TrashIcon } from "@nens/ranalib";

import { AppDispatch } from "../../App";
import PermissionButton from "../../components/PermissionButton";
import { remindUserToPressPlay } from "../../state/mainGui";
import { getSimulationStatus } from "../../state/simulation";
import { menuExistingItemCard, menuExistingItemCardRow } from "../../styles/styleComposition";
import { ControlType } from "../../utils/authorizationUtils";
import { timeConverter } from "../../utils/timeConverter";

interface ExistingItemCardProps {
  children: React.ReactNode;
  onDelete?: () => void;
  onStop?: () => void;
  start?: number;
  end?: number;
  controlType?: ControlType;
  remindUserToPressPlay: () => void;
}

function ExistingItemCard({
  children,
  onDelete,
  onStop,
  start,
  end,
  controlType,
  remindUserToPressPlay,
}: ExistingItemCardProps) {
  const simulationStatus = useSelector(getSimulationStatus);
  const simulationTime = simulationStatus.time;

  const showDelete =
    onDelete &&
    controlType &&
    simulationTime !== undefined &&
    start !== undefined &&
    simulationTime <= start;

  const status =
    start !== undefined
      ? // Note the "+1" - because when we stop events, we do it by setting their duration
        // so that they end 1 second after the current time; then it should show as finished here.
        simulationTime < start
        ? "future"
        : end !== undefined && simulationTime + 1 >= end
        ? "finished"
        : "active"
      : "-";

  const showStop = !showDelete && onStop && status === "active";

  return (
    <div className={menuExistingItemCard}>
      {children}
      {start !== undefined ? (
        <div className={menuExistingItemCardRow}>
          <span>
            <FormattedMessage id="existingWindMenu.start" defaultMessage="Start" />
          </span>
          <span>{timeConverter(start)} </span>
        </div>
      ) : null}
      {end !== undefined ? (
        <div className={menuExistingItemCardRow}>
          <span>
            <FormattedMessage id="existingWindMenu.end" defaultMessage="End" />
          </span>
          <span>{timeConverter(end)} </span>
        </div>
      ) : null}
      <div className={menuExistingItemCardRow}>
        <span>
          <FormattedMessage id="existingRainMenu.status_label" defaultMessage="Status" />
        </span>
        <span>
          {status === "active" ? (
            <FormattedMessage id="existingRainMenu.status_label_active" defaultMessage="Active" />
          ) : status === "finished" ? (
            <FormattedMessage
              id="existingRainMenu.status_label_finished"
              defaultMessage="Finished"
            />
          ) : status === "future" ? (
            <FormattedMessage
              id="existingRainMenu.status_label_not_active"
              defaultMessage="Not active yet"
            />
          ) : (
            "-"
          )}
        </span>
      </div>
      {showDelete ? (
        <PermissionButton
          controlType={controlType!}
          formattedMessage="Delete"
          doSubmit={() => {
            onDelete!();
            remindUserToPressPlay();
          }}
          leftIcon={<TrashIcon />}
        />
      ) : showStop ? (
        <PermissionButton
          controlType={controlType!}
          formattedMessage="Stop"
          doSubmit={() => {
            onStop!();
            remindUserToPressPlay();
          }}
          leftIcon={<CancelIcon />}
        />
      ) : null}
    </div>
  );
}

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    remindUserToPressPlay: () => dispatch(remindUserToPressPlay()),
  };
};

export default connect(null, mapDispatchToProps)(ExistingItemCard);
